import React, {useState, useEffect, useContext} from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Chip from '@mui/material/Chip';
import {I18nReactContext} from "../../../contexts/I18nContext";
import './AutoComplete.scss';
import {some, filter} from "lodash";

const icon = <CheckBoxOutlineBlankIcon fontSize="small"/>;
const checkedIcon = <CheckBoxIcon fontSize="small"/>;
export const MultiSelectAutocomplete = ({
                                            fetchOptions,
                                            onSelectionChange,
                                            isSingleSelect = false,
                                            initialSelectedValues,
                                            label,
                                            disabled,
                                            enableSelectAll,
                                            selectAllText,
                                            error = false,
                                            refreshTrigger // Optional prop to trigger options refresh
                                        }) => {
    const {i18n} = useContext(I18nReactContext);
    const [focused, setFocused] = useState(false);
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [options, setOptions] = useState([]);
    const [selectedItems, setSelectedItems] = useState(() => {
        if (initialSelectedValues) {
            return initialSelectedValues;
        }
        return isSingleSelect ? null : [];
    });

    // Debounce the user input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300); // Delay of 300ms

        return () => {
            clearTimeout(handler);
        };
    }, [query]);

    const fetchData = async () => {
        try {
            let data = await fetchOptions({query: debouncedQuery});

            // Auto-select items that have isSelected: true flag
            const preSelectedItems = data.filter(option => option.isSelected === true);
            if (preSelectedItems.length > 0 && selectedItems.length === 0) {
                setSelectedItems(preSelectedItems);
                onSelectionChange(preSelectedItems);
            }

            if (enableSelectAll && !isSingleSelect) {
                //check if select all is already present
                const selectAll = data.find(option => option.value === 'all');

                let actualOrganizations = data.filter(option => option.value !== 'all');

                if (!selectAll && actualOrganizations.length > 1) {
                    data.unshift({
                        value: 'all',
                        text: selectAllText || i18n.translate('autoComplete.selectAll')
                    });
                }
            }

            setOptions(data || []);
        } catch (error) {
            console.error("Error fetching options:", error);
        }
    };

    // Fetch options based on the debounced query or when refreshTrigger changes
    useEffect(() => {
        // Clear previous options before fetching new ones to prevent stale data mixing
        setOptions([]);
        fetchData();
    }, [debouncedQuery, refreshTrigger]);

    const handleSelectionChange = (_event, value) => {
        // Handle single select mode
        if (isSingleSelect) {
            setSelectedItems(value);
            onSelectionChange(value);
            return;
        }

        // Handle multi-select mode with "select all" logic
        const hasAllOption = some(value, item => item.value === 'all');
        const hadAllOption = some(selectedItems, item => item.value === 'all');

        // Case 1: User clicked "All" to select all options
        if (hasAllOption && !hadAllOption) {
            const actualOptions = filter(options, item => item.value !== 'all');
            setSelectedItems(options);
            onSelectionChange(actualOptions);
            return;
        }

        // Case 2: User unchecked "All" - clear everything
        if (hadAllOption && !hasAllOption) {
            setSelectedItems([]);
            onSelectionChange([]);
            return;
        }

        // Case 3: Normal multi-select behavior
        setSelectedItems(filter(value, item => item.value !== 'all'));
        onSelectionChange(filter(value, item => item.value !== 'all'));
    };

    return (
        <Autocomplete
            className="multi-select-autocomplete-limit-tags"
            limitTags={2}
            multiple={!isSingleSelect} // Enable multiple selections based on the prop
            disabled={disabled}
            options={options} // Options from the API
            value={selectedItems} // Controlled selected values
            onChange={handleSelectionChange} // Handle changes in selection
            onInputChange={(event, newInputValue) => setQuery(newInputValue)} // Update query for fetching options
            filterOptions={(x) => x} // Disable MUI's default filtering (API handles filtering)
            renderTags={(value, getTagProps) => {
                // If all options are selected, show a single "All" chip
                const isAllSelected = value.length === options.length && options.length > 0;
                if (isAllSelected) {
                    const allOption = options.find(opt => opt.value === 'all');
                    const allText = allOption?.text || selectAllText || i18n.translate('autoComplete.selectAll');
                    return (
                        <Chip
                            {...getTagProps({ index: 0 })}
                            label={allText}
                            style={{ maxWidth: '180px' }}
                        />
                    );
                }

                const limitTags = 2;
                const numTags = value.length;
                const limitTagsReached = numTags > limitTags;

                return (
                    <>
                        {value.slice(0, limitTags).map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.value}
                                label={option.text}
                                style={{ maxWidth: '150px' }}
                            />
                        ))}
                        {limitTagsReached && (
                            <Chip
                                label={`+${numTags - limitTags}`}
                                style={{
                                    backgroundColor: '#e0e0e0',
                                    fontWeight: 'bold'
                                }}
                            />
                        )}
                    </>
                );
            }}
            isOptionEqualToValue={(option, value) => option?.value === value?.value} // Prevent duplicate options
            renderInput={(params) => (
                <TextField {...params} label={label || i18n.translate('autoComplete.search')}
                   error={error}
                   variant="outlined"
                   fullWidth
                   onFocus={() => setFocused(true)}
                   onBlur={() => {setFocused(false)}}
                   InputLabelProps={{style: { color: (error && focused) ? 'red' : 'black' }}}
              />
            )}
            renderOption={(props, option, {selected}) => {
                const {key, ...optionProps} = props;
                return (
                    <li key={key} {...optionProps}>
                        {!isSingleSelect ? <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{marginRight: 8}}
                            checked={selected}
                        /> : ''}
                        {option.text}
                    </li>
                );
            }}
            disableCloseOnSelect={!isSingleSelect} // Keep dropdown open for multi-select
            getOptionLabel={(option) => option?.text || ''} // Display option text
        />
    );
};

